import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'expo-router';
import { useAuthStore, useChatInputStore } from '@/store';
import { useBrowserConnectionStore as store } from '@/store/browserConnection';
import { BROWSER_EXTENSION_ORIGINS, formatBrowserContext } from '@/lib/browserPanel';
import { getChatInputContextIdFromPathname } from '@/lib/chatInputContext';
import { createBrowserWorkClient } from '@/lib/browserPanelWork';
import { fetchWithAuth } from '@/lib/api/client';

const CHANNEL = 'gab-web-browser-v1';
const signedIn = () => {
  const s = useAuthStore.getState();
  return !!(s._hasHydrated && s.token && s.user?.id && !s.user.flags?.isGhost && !s._isSigningOut);
};
export default function BrowserConnectionBridge() {
  const pathname = usePathname(), userId = useAuthStore(s => s.user?.id), authenticated = useAuthStore(s => !!s._hasHydrated && !!s.token && !s.user?.flags?.isGhost);
  const enabled = store(s => s.enabled), available = store(s => s.available);
  const [rememberedSupported, setRememberedSupported] = useState(false);
  const [connectionPending, setConnectionPending] = useState(false);
  const [extensionInstance, setExtensionInstance] = useState(null);
  const previousInstance = useRef(null);
  const supportsRemembered = useRef(false), autoAttempt = useRef(null), connecting = useRef(false);
  const nonce = useRef(null), currentPath = useRef(pathname), client = useRef(null), pending = useRef(new Map());
  currentPath.current = pathname;
  const previous = useRef({ userId, pathname });
  const send = useRef(() => {});
  const extensionRequest = useRef(null);
  const bindingAcknowledgement = useRef(false);
  const generation = useRef(0);
  const cancelPending = useRef(() => {});
  const connectionError = useRef(''), browserOwner = useRef(null);

  useEffect(() => {
    if (window.parent !== window) return;
    nonce.current = crypto.randomUUID();
    let active = true;
    let discovered = false, missedReady = 0;
    const post = (type, extra = {}) => window.postMessage({ channel: CHANNEL, direction: 'page', nonce: nonce.current, type, ...extra }, location.origin);
    send.current = post;
    const request = (type, extra = {}) => new Promise((resolve, reject) => {
      const requestId = crypto.randomUUID();
      const timer = setTimeout(() => { pending.current.delete(requestId); reject(Error('Browser request expired. Inspect before retrying.')); }, 35000);
      pending.current.set(requestId, { resolve: result => { clearTimeout(timer); resolve(result); }, cancel: () => { clearTimeout(timer); reject(Error('Browser connection changed.')); } });
      post(type, { requestId, ...extra });
    });
    extensionRequest.current = request;
    cancelPending.current = () => {
      generation.current++;
      for (const request of pending.current.values()) request.cancel?.();
      pending.current.clear();
      store.setState({ busy: null, picker: false, pages: [], selected: [] });
    };
    const disconnect = ({ forget = false } = {}) => {
      cancelPending.current();
      client.current?.stop(); client.current = null; post('disconnect', { forget });
      store.setState({ enabled: false, phase: 'idle', busy: null, picker: false, pages: [], selected: [], status: 'Browser disconnected.' });
    };
    const onMessage = event => {
      const data = event.data;
      if (!active || event.source !== window || event.origin !== location.origin || data?.channel !== CHANNEL || data.direction !== 'extension' || data.nonce !== nonce.current) return;
      if (data.type === 'ready' && BROWSER_EXTENSION_ORIGINS.includes(`chrome-extension://${data.extensionId}`)) {
        discovered = true; missedReady = 0;
        if (typeof data.instanceId === 'string' && data.instanceId !== previousInstance.current) {
          if (previousInstance.current !== null) disconnect();
          previousInstance.current = data.instanceId;
          setExtensionInstance(data.instanceId);
        }
        bindingAcknowledgement.current = Array.isArray(data.capabilities) && data.capabilities.includes('binding-acknowledgement');
        supportsRemembered.current = Array.isArray(data.capabilities) && data.capabilities.includes('remembered-connection');
        setRememberedSupported(supportsRemembered.current);
        store.setState({ available: true });
      }
      if (data.type === 'state') {
        if (!data.enabled) disconnect();
        const enabled = data.enabled === true && signedIn();
        browserOwner.current = enabled && typeof data.leaseOwner === 'string' ? data.leaseOwner : null;
        store.setState({ enabled, phase: enabled ? 'connecting' : 'idle', status: !enabled && connectionError.current ? connectionError.current : data.message || '' });
        if (data.enabled && !signedIn()) disconnect();
      }
      if (data.type === 'notice') store.setState({ status: String(data.message || '').slice(0, 500) });
      if (data.type === 'reply' && pending.current.has(data.requestId)) { pending.current.get(data.requestId).resolve(data.result); pending.current.delete(data.requestId); }
    };
    window.addEventListener('message', onMessage);
    const hello = () => post('hello'); hello();
    const timer = setInterval(() => {
      if (discovered && ++missedReady > 4) {
        discovered = false; disconnect(); store.setState({ available: false, status: 'The extension is unavailable. Check that Gab AI is enabled in this browser, then reload this page.' });
      }
      hello();
    }, 1500);
    const isCurrent = epoch => active && epoch === generation.current && signedIn() && store.getState().enabled;
    const showError = (e, epoch) => { if (isCurrent(epoch)) store.setState({ busy: null, status: e.message || 'Browser connection unavailable.' }); };
    store.setState({
      connect: ({ automatic = false } = {}) => {
        if (!signedIn() || !store.getState().available || connecting.current) return;
        if (automatic && !supportsRemembered.current) return;
        connectionError.current = '';
        const epoch = generation.current;
        if (!automatic) store.setState({ phase: 'approving', status: 'Choose Connect in the Gab AI extension window.' });
        if (!bindingAcknowledgement.current) { post('connect'); return; }
        connecting.current = true; setConnectionPending(true);
        void (async () => {
          const identity = supportsRemembered.current ? await fetchWithAuth('/json/extension/work/authorize', {
            method: 'POST', body: {}, networkRetries: 0, timeoutMs: 10000,
          }) : null;
          if (!active || epoch !== generation.current || !signedIn()) return;
          const result = await request('connect', { ...(identity ? { proof: identity.proof } : {}), automatic });
          if (automatic && result?.needsApproval) return;
          if (!result?.ok) throw Error(result?.error || 'Could not connect your browser.');
        })().catch(error => {
          if (!automatic && active && epoch === generation.current) store.setState({ phase: 'idle', status: error.message });
        }).finally(() => { connecting.current = false; if (active) setConnectionPending(false); });
      },
      disconnect: () => disconnect({ forget: true }), release: disconnect, options: () => post('options'),
      list: () => {
        if (!store.getState().enabled || store.getState().busy) return;
        const epoch = generation.current; store.setState({ busy: 'listing', picker: true, status: '' });
        return request('tabs').then(result => {
        if (!isCurrent(epoch)) return;
        if (!result?.ok) throw Error(result?.error || 'Could not list browser pages.');
        store.setState({ busy: null, pages: result.tabs || [], selected: [], picker: true, status: '' });
      }).catch(e => showError(e, epoch)); },
      add: () => {
        if (!store.getState().enabled || store.getState().busy || !store.getState().selected.length) return;
        const epoch = generation.current; store.setState({ busy: 'adding', status: '' });
        const owner = useAuthStore.getState().user?.id, sourcePath = currentPath.current;
        return request('read', { tabIds: store.getState().selected }).then(result => {
        if (!isCurrent(epoch)) return;
        if (!result?.ok) throw Error(result?.error || 'Could not read browser pages.');
        const texts = (result.contexts || []).slice(0, 50).map(formatBrowserContext).filter(Boolean);
        const contextId = getChatInputContextIdFromPathname(currentPath.current);
        if (!contextId || !signedIn() || !store.getState().enabled || owner !== useAuthStore.getState().user?.id || sourcePath !== currentPath.current) return;
        const input = useChatInputStore.getState(), prior = input.inputStates[contextId]?.text || '';
        input.setContext(contextId); input.setText([prior, ...texts].filter(Boolean).join('\n\n'));
        store.setState({ busy: null, picker: false, status: `Added ${texts.length} ${texts.length === 1 ? 'page' : 'pages'} to your draft. Review before sending.${result.skipped ? ` ${result.skipped} skipped.` : ''}` });
        }).catch(e => showError(e, epoch));
      },
    });
    return () => { active = false; clearInterval(timer); disconnect(); window.removeEventListener('message', onMessage); store.setState({ available: false, status: '' }); };
  }, []);

  useEffect(() => {
    if (window.parent !== window) return;
    const prior = previous.current;
    if (prior.pathname !== pathname) cancelPending.current();
    if (!authenticated || !signedIn() || prior.userId !== userId || (/^\/c\//.test(prior.pathname) && prior.pathname !== pathname)) store.getState().release();
    previous.current = { userId, pathname };
  }, [pathname, userId, authenticated]);

  useEffect(() => {
    if (!available || !authenticated) autoAttempt.current = null;
    if (!available || !rememberedSupported || enabled || connectionPending || !signedIn() || window.parent !== window
        || !/^\/(?:app|c\/[a-f\d]{24})$/.test(pathname)) return;
    const key = `${userId}:${pathname}:${extensionInstance}`;
    if (autoAttempt.current === key) return;
    autoAttempt.current = key;
    store.getState().connect({ automatic: true });
  }, [available, rememberedSupported, enabled, connectionPending, userId, authenticated, pathname, extensionInstance]);

  useEffect(() => {
    if (window.parent !== window || !enabled || !store.getState().enabled || !signedIn()) return;
    const conversationId = pathname.match(/^\/c\/([a-f\d]{24})$/)?.[1];
    if (!conversationId) { store.setState({ phase: 'connected', status: 'Browser connected. Ready for your next message.' }); return; }
    store.setState({ phase: 'connecting', status: 'Connecting to this chat…' });
    let active = true;
    const requestCommand = command => {
      const requestId = crypto.randomUUID();
      const timer = setTimeout(() => {
        pending.current.delete(requestId);
        void next.result({ connectionId: command.connectionId, id: command.id, result: { ok: false, error: 'Browser response timed out. Inspect before retrying.' } });
      }, 28000);
      pending.current.set(requestId, { resolve: result => { clearTimeout(timer); void next.result({ connectionId: command.connectionId, id: command.id, result }); }, cancel: () => clearTimeout(timer) });
      send.current('command', { requestId, command });
    };
    const next = createBrowserWorkClient({ request: fetchWithAuth, conversationId, leaseOwner: browserOwner.current,
      current: () => active && signedIn() && useAuthStore.getState().user?.id === userId && store.getState().enabled,
      send: (type, data) => {
        if (type === 'work-connected') {
          // Existing store installs do not acknowledge bind. Keep that protocol
          // working until the extension update arrives, without waiting for a
          // reply it cannot send. New installs confirm the actual chat binding.
          if (!bindingAcknowledgement.current) {
            send.current('bind', { connectionId: data.id, conversationId });
            store.setState({ phase: 'connected', status: 'Browser connected. Ready for your requests.' });
            return;
          }
          void extensionRequest.current('bind', { connectionId: data.id, conversationId }).then(result => {
            if (!active) return;
            if (!result?.ok) throw Error(result?.error || 'Could not connect this chat.');
            store.setState({ phase: 'connected', status: 'Connected to this chat. Ready for your requests.' });
          }).catch(error => { if (active) { connectionError.current = error.message; store.getState().release(); store.setState({ status: error.message }); } });
        }
        if (type === 'work-command') requestCommand(data.command);
        if (type === 'work-reconnecting') store.setState({ phase: 'connecting', status: 'Reconnecting to your chat…' });
        if (type === 'work-restored') store.setState({ phase: 'connected', status: 'Connected to this chat.' });
        if (type === 'work-error') { connectionError.current = data.message; store.getState().release(); store.setState({ status: data.message }); }
      } });
    client.current = next; void next.start();
    return () => { active = false; next.stop(); if (client.current === next) client.current = null; };
  }, [pathname, userId, enabled]);
  return null;
}
